/**
 * Widget Management Page
 * Create, view, and revoke embed widget API keys
 * Shows the embed code for each key
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDateTime } from '@/lib/utils';
import {
  Globe,
  Plus,
  Copy,
  Trash2,
  CheckCircle,
  AlertTriangle,
  Code,
  Key,
} from 'lucide-react';

interface WidgetKey {
  id: string;
  key: string;
  name: string;
  allowedDomains: string[];
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  submissionCount: number;
  _plainKey?: string; // Only present right after creation
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 text-xs gap-1">
      {copied ? <CheckCircle className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied!' : label}
    </Button>
  );
}

function EmbedCode({ widgetKey, apiUrl }: { widgetKey: string; apiUrl: string }) {
  const [show, setShow] = useState(false);

  const scriptTag = `<script src="${apiUrl}/api/helpdesk/widget.js?key=${widgetKey}" defer></script>`;

  return (
    <div className="mt-3">
      <Button
        variant="ghost"
        size="sm"
        className="text-xs gap-1 mb-2"
        onClick={() => setShow(!show)}
      >
        <Code className="h-3.5 w-3.5" />
        {show ? 'Hide embed code' : 'Show embed code'}
      </Button>
      {show && (
        <div className="bg-muted/70 rounded-md p-3 text-xs font-mono break-all relative">
          <div className="absolute top-2 right-2">
            <CopyButton text={scriptTag} label="Copy" />
          </div>
          <pre className="whitespace-pre-wrap pr-16">{scriptTag}</pre>
          <p className="text-muted-foreground mt-2 font-sans font-normal not-italic">
            Paste this script tag before <code className="font-mono">&lt;/body&gt;</code> on any page where you want the support widget to appear.
          </p>
        </div>
      )}
    </div>
  );
}

export function WidgetManagementPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDomains, setNewKeyDomains] = useState('');
  const [newlyCreated, setNewlyCreated] = useState<WidgetKey | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || window.location.origin;

  const { data, isLoading } = useQuery({
    queryKey: ['widget-keys'],
    queryFn: () => api.getWidgetKeys(),
  });

  const createMutation = useMutation({
    mutationFn: (body: { name: string; allowedDomains: string[] }) => api.createWidgetKey(body),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['widget-keys'] });
      if (result.data) {
        setNewlyCreated(result.data as WidgetKey);
      }
      setShowCreate(false);
      setNewKeyName('');
      setNewKeyDomains('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.revokeWidgetKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widget-keys'] });
      setConfirmDelete(null);
    },
  });

  const keys = (data?.data || []) as WidgetKey[];

  const handleCreate = () => {
    if (!newKeyName.trim()) return;
    const domains = newKeyDomains
      .split(',')
      .map((d) => d.trim())
      .filter(Boolean);
    createMutation.mutate({ name: newKeyName.trim(), allowedDomains: domains });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Widget Management</h1>
          <p className="text-muted-foreground">
            Create embeddable widget keys for external sites to submit support tickets
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Widget Key
        </Button>
      </div>

      {/* How it works */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-5">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            How the Help Desk Widget Works
          </h3>
          <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
            <li>Create a widget key and optionally restrict it to specific domains</li>
            <li>Copy the embed code and paste it into any website's HTML before <code className="font-mono text-xs bg-muted px-1 rounded">&lt;/body&gt;</code></li>
            <li>A floating support button appears on the page — visitors click it to open the ticket form</li>
            <li>On submission, a ticket is created in your Help Desk dashboard</li>
            <li>You receive an SMS notification at your configured admin phone number</li>
          </ol>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
              Rate limited (5 per IP/15min)
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
              Domain allowlist support
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
              Anti-bot honeypot
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
              SMS admin alerts
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Newly created key banner */}
      {newlyCreated && (
        <Card className="border-green-300 bg-green-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-green-800">Widget key created!</p>
                <p className="text-sm text-green-700 mt-1">
                  Copy your key now — it will not be shown again in full.
                </p>
                <div className="mt-3 bg-white border border-green-200 rounded p-3 flex items-center justify-between gap-3">
                  <code className="text-sm font-mono text-green-900 break-all">{newlyCreated._plainKey}</code>
                  <CopyButton text={newlyCreated._plainKey || ''} label="Copy Key" />
                </div>
                <EmbedCode widgetKey={newlyCreated._plainKey || ''} apiUrl={apiUrl} />
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3 text-green-700"
                  onClick={() => setNewlyCreated(null)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Form */}
      {showCreate && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="h-4 w-4" />
              Create New Widget Key
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="key-name">Key Name *</Label>
              <Input
                id="key-name"
                placeholder="e.g. Main Website, Client Portal"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="key-domains">Allowed Domains (optional)</Label>
              <Input
                id="key-domains"
                placeholder="example.com, app.example.com (comma-separated)"
                value={newKeyDomains}
                onChange={(e) => setNewKeyDomains(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave blank to allow all domains. When set, the widget will only accept submissions from these domains.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={!newKeyName.trim() || createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Key'}
              </Button>
              <Button variant="outline" onClick={() => { setShowCreate(false); setNewKeyName(''); setNewKeyDomains(''); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keys List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : keys.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Globe className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">No widget keys yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first widget key to start embedding the help desk form</p>
            <Button className="mt-4" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Widget Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {keys.map((key) => (
            <Card key={key.id} className={!key.isActive ? 'opacity-60' : ''}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{key.name}</span>
                      {key.isActive ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          <CheckCircle className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                          <AlertTriangle className="h-3 w-3" /> Revoked
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-2">
                      <code className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">
                        {key.key}
                      </code>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span>Created: {formatDateTime(key.createdAt)}</span>
                      <span>Last used: {key.lastUsedAt ? formatDateTime(key.lastUsedAt) : 'Never'}</span>
                      <span>{key.submissionCount} submissions</span>
                    </div>

                    {key.allowedDomains.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {key.allowedDomains.map((domain) => (
                          <span key={domain} className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-0.5 rounded">
                            <Globe className="h-3 w-3" /> {domain}
                          </span>
                        ))}
                      </div>
                    )}

                    {key.isActive && (
                      <EmbedCode widgetKey={key.key.includes('...') ? '(copy from creation)' : key.key} apiUrl={apiUrl} />
                    )}
                  </div>

                  {key.isActive && (
                    <div className="shrink-0">
                      {confirmDelete === key.id ? (
                        <div className="flex flex-col gap-2 items-end">
                          <p className="text-xs text-destructive font-medium">Revoke this key?</p>
                          <div className="flex gap-2">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deleteMutation.mutate(key.id)}
                              disabled={deleteMutation.isPending}
                            >
                              Revoke
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(key.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Security Notes */}
      <Card className="border-orange-200 bg-orange-50/50">
        <CardContent className="p-5">
          <h3 className="font-semibold text-sm flex items-center gap-2 text-orange-800">
            <AlertTriangle className="h-4 w-4" />
            Security Best Practices
          </h3>
          <ul className="mt-2 text-sm text-orange-700 space-y-1 list-disc list-inside">
            <li>Always set allowed domains to restrict widget submissions to your authorized sites</li>
            <li>Revoke unused keys immediately to minimize attack surface</li>
            <li>Each key is unique — use separate keys for different applications/sites</li>
            <li>Widget keys are public-facing; they have strict rate limiting applied</li>
            <li>Configure <code className="font-mono text-xs bg-orange-100 px-1 rounded">ADMIN_PHONE_NUMBER</code> for SMS alerts on new submissions</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
